// CB:BEGIN admin-alerts-panel
/**
 * AdminAlertsPanel — "Needs Attention" section for the admin command center.
 *
 * Shows stalled projects that require admin review. Each row includes:
 * - Project address
 * - Current status + days stalled
 * - Quick "Review" button
 *
 * Silent-fail on API error (non-blocking UI component).
 */
import React, { useState, useEffect } from 'react';
import { useApi } from '../../lib/useApi';

interface StalledProject {
  id: string;
  address?: string;
  status: string;
  statusEnteredAt?: string;
  daysStalled?: number;
}

interface AdminAlertsPanelProps {
  onNavigate?: (screen: string) => void;
}

function daysAgo(dateStr?: string, fallback?: number): number | null {
  if (dateStr) {
    const d = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86_400_000);
    return d;
  }
  return fallback ?? null;
}

function stalledColor(days: number | null): string {
  if (days == null) return '#6b7280';
  if (days >= 7) return '#dc2626';
  if (days >= 3) return '#d97706';
  return '#374151';
}

const AlertTriangleIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ flexShrink: 0 }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const AdminAlertsPanel: React.FC<AdminAlertsPanelProps> = ({ onNavigate }) => {
  const api = useApi();
  const [stalled, setStalled] = useState<StalledProject[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get('/api/admin/projects/stalled') as any;
        const projects: StalledProject[] = data?.projects ?? data ?? [];
        setStalled(projects.slice(0, 8));
      } catch {
        // silent fail
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  if (!loaded || stalled.length === 0) return null;

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #fecaca',
      borderRadius: 10,
      overflow: 'hidden',
      marginBottom: 20,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        background: '#fef2f2',
        borderBottom: '1px solid #fecaca',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#991b1b' }}>
          <AlertTriangleIcon />
          <span style={{ fontSize: 14, fontWeight: 700 }}>
            Needs Attention — {stalled.length} stalled project{stalled.length !== 1 ? 's' : ''}
          </span>
        </div>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>Review and unblock</span>
      </div>

      {/* Rows */}
      <div>
        {stalled.map((p, idx) => {
          const days = daysAgo(p.statusEnteredAt, p.daysStalled);
          const color = stalledColor(days);
          const isLast = idx === stalled.length - 1;

          return (
            <div
              key={p.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 16px',
                borderBottom: isLast ? 'none' : '1px solid #f9fafb',
                background: '#fff',
                gap: 12,
              }}
            >
              {/* Info */}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.address || `Project ${p.id.slice(-8)}`}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '1px 6px',
                    background: '#f3f4f6', color: '#374151', borderRadius: 4,
                  }}>
                    {p.status}
                  </span>
                  {days != null && (
                    <span style={{ fontSize: 12, color, fontWeight: days >= 3 ? 600 : 400 }}>
                      {days === 0 ? 'today' : `${days}d stalled`}
                    </span>
                  )}
                </div>
              </div>

              {/* Action */}
              {onNavigate && (
                <button
                  type="button"
                  onClick={() => onNavigate(`admin-project-edit-${p.id}`)}
                  style={{
                    background: '#dc2626',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Review
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminAlertsPanel;
// CB:END admin-alerts-panel
